import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import { TaskState, WorkloadTask } from 'shovel-lib/types';

import { getAllCourses } from '@state/calendar/selectors';
import { RootState } from '@state/types';

import { SELECTION_ERROR_MSG, STANDALONE_SECTION_KEY, categorySectionKey, sourceSectionKey } from './constants';
import {
  EstimatorCategory,
  EstimatorReadingSource,
  EstimatorState,
  EstimatorTask,
  EstimatorUiState,
  SelectionType
} from './types';
import {
  addId,
  buildEstimateRequest,
  mapWorkloadToEstimator,
  patchTask,
  readingMinutes,
  removeId,
  removeKey,
  serverTaskChange,
  ServerTaskChange,
  toggleKey
} from './utils';

const emptyUi = (): EstimatorUiState => ({
  openTaskId: null,
  collapsedSectionKeys: [],
  selectedIds: [],
  flashingIds: [],
  lastCheckedId: null,
  selectionError: null,
  editingName: null,
  bulkConverting: false,
  bulkMovingCategory: false,
  openCustomKeys: [],
  toasts: {},
  scrollToTaskId: null
});

const emptyState = (): EstimatorState => ({
  categories: [],
  sources: [],
  tasks: [],
  originals: { categories: {}, sources: {}, titles: {} },
  ui: emptyUi()
});

const closePickers = (ui: EstimatorUiState): EstimatorUiState => ({
  ...ui,
  editingName: null,
  bulkConverting: false,
  bulkMovingCategory: false
});

const flash = (ui: EstimatorUiState, ids: number[]): EstimatorUiState => ({
  ...ui,
  flashingIds: ids.reduce((acc, id) => addId(acc, id), ui.flashingIds)
});

const recomputeSourceMinutes = (tasks: EstimatorTask[], sources: EstimatorReadingSource[], sourceId: number) => {
  const source = sources.find(item => item.id === sourceId);
  if (!source) return { tasks, flashed: [] as number[] };
  const flashed: number[] = [];
  const next = tasks.map(task => {
    if (task.kind !== 'reading' || task.sourceId !== sourceId || task.pages == null) return task;
    flashed.push(task.id);
    return { ...task, minutes: readingMinutes(task.pages, source.secondsPerPage) };
  });
  return { tasks: next, flashed };
};

const applyTrash = (task: EstimatorTask): EstimatorTask => ({
  ...task,
  trashed: true,
  minutes: null,
  overridden: false,
  overriddenThisSession: false,
  pages: null,
  from: null,
  to: null,
  rangeLabel: null,
  rawPagesInput: null
});

const mutateToReading = (task: EstimatorTask, sourceId: number): EstimatorTask => ({
  ...task,
  kind: 'reading',
  categoryId: null,
  sourceId,
  overridden: false,
  overriddenThisSession: false,
  pages: null,
  from: null,
  to: null,
  rangeLabel: null,
  rawPagesInput: null,
  detectedPages: null,
  detectedFrom: null,
  detectedTo: null,
  detectedRangeLabel: null,
  detectedRawPagesInput: null,
  minutes: null
});

const mutateToRegular = (task: EstimatorTask, categoryId: number | null): EstimatorTask => ({
  ...task,
  kind: 'regular',
  categoryId,
  sourceId: null,
  overridden: false,
  overriddenThisSession: false,
  pages: null,
  from: null,
  to: null,
  rangeLabel: null,
  rawPagesInput: null,
  detectedPages: null,
  detectedFrom: null,
  detectedTo: null,
  detectedRangeLabel: null,
  detectedRawPagesInput: null,
  minutes: null
});

const mutateCategory = (task: EstimatorTask, categoryId: number | null): EstimatorTask => {
  if (task.overridden) return { ...task, categoryId };
  return { ...task, categoryId, minutes: null, estimatedAtOpen: false };
};

type Action =
  | { type: 'RESET'; payload: Pick<EstimatorState, 'categories' | 'sources' | 'tasks' | 'originals'> }
  | { type: 'CLEAR_FLASH'; ids: number[] }
  | { type: 'CLEAR_SCROLL' }
  | { type: 'TOGGLE_COLLAPSE'; key: string }
  | { type: 'TOGGLE_TASK_POPUP'; taskId: number }
  | { type: 'CLOSE_TASK_POPUP' }
  | { type: 'START_EDIT_NAME'; nameType: { type: 'category' | 'source' | 'task'; id: number } }
  | { type: 'COMMIT_EDIT_NAME'; value: string }
  | { type: 'CANCEL_EDIT' }
  | { type: 'COMMIT_EMOJI'; categoryId: number; emoji: string }
  | { type: 'CONVERT_TO_READING'; taskId: number; sourceId: number }
  | { type: 'CONVERT_TO_REGULAR'; taskId: number; categoryId: number | null }
  | { type: 'TOGGLE_BULK_CONVERT' }
  | { type: 'BULK_CONVERT_TO_READING'; sourceId: number }
  | { type: 'BULK_CONVERT_TO_REGULAR'; categoryId: number | null }
  | { type: 'REASSIGN_CATEGORY'; taskId: number; categoryId: number | null }
  | { type: 'TOGGLE_BULK_MOVE_CATEGORY' }
  | { type: 'BULK_REASSIGN_CATEGORY'; categoryId: number | null }
  | { type: 'SELECT_TASK'; taskId: number; wantChecked: boolean; orderedIds: number[]; shiftKey: boolean }
  | { type: 'SELECT_GROUP'; ids: number[]; kind: 'regular' | 'reading' }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'APPLY_CATEGORY_DEFAULT'; categoryId: number; minutes: number; onlyUnestimated?: boolean }
  | { type: 'SET_TASK_MINUTES'; taskId: number; minutes: number }
  | { type: 'CLEAR_TASK'; taskId: number }
  | { type: 'RESET_TO_DEFAULT'; taskId: number }
  | { type: 'TRASH_TASK'; taskId: number }
  | { type: 'UNTRASH_TASK'; taskId: number }
  | { type: 'BULK_SET_MINUTES'; minutes: number }
  | { type: 'BULK_USE_DEFAULT' }
  | { type: 'BULK_TRASH' }
  | {
      type: 'SET_PAGES';
      taskId: number;
      pages: number;
      from: number | null;
      to: number | null;
      rangeLabel: string;
      rawPagesInput: string;
    }
  | { type: 'CLEAR_PAGES'; taskId: number }
  | { type: 'RESTORE_DETECTED'; taskId: number }
  | { type: 'RESET_SOURCE_DETECTED'; sourceId: number }
  | { type: 'SET_RATE'; sourceId: number; secondsPerPage: number }
  | {
      type: 'BULK_SET_PAGES';
      pages: number;
      from: number | null;
      to: number | null;
      rangeLabel: string;
      rawPagesInput: string;
    }
  | { type: 'BULK_RESTORE_DETECTED' }
  | { type: 'RESET_CATEGORY'; categoryId: number }
  | { type: 'RESET_STANDALONE' }
  | { type: 'TOGGLE_CUSTOM'; key: string }
  | { type: 'SYNC_FROM_SERVER'; changes: ServerTaskChange[]; courseCategories: EstimatorCategory[] };

const taskKind = (task: EstimatorTask | undefined): SelectionType => {
  if (!task) return null;
  return task.kind;
};

const selectionType = (tasks: EstimatorTask[], selectedIds: number[]): SelectionType => {
  if (selectedIds.length === 0) return null;
  const first = tasks.find(task => task.id === selectedIds[0]);
  return taskKind(first);
};

const reducer = (state: EstimatorState, action: Action): EstimatorState => {
  switch (action.type) {
    case 'RESET': {
      // Every category opens with its tasks folded: its one time pick covers them, and a long course read as a list
      // of every task to estimate. A category's list opens on "See all", or when a task is moved or converted into it.
      const closedCategoryIds = action.payload.categories
        .filter(category => action.payload.tasks.some(task => task.kind === 'regular' && task.categoryId === category.id))
        .map(category => categorySectionKey(category.id));
      const closedSourceKeys = action.payload.sources
        .filter(source => {
          const sourceTasks = action.payload.tasks.filter(task => task.kind === 'reading' && task.sourceId === source.id);
          return sourceTasks.length > 0 && sourceTasks.every(task => task.estimatedAtOpen);
        })
        .map(source => sourceSectionKey(source.id));
      return {
        ...action.payload,
        ui: { ...emptyUi(), collapsedSectionKeys: [...closedCategoryIds, ...closedSourceKeys] }
      };
    }
    case 'CLEAR_FLASH':
      return { ...state, ui: { ...state.ui, flashingIds: state.ui.flashingIds.filter(id => !action.ids.includes(id)) } };
    case 'CLEAR_SCROLL':
      return { ...state, ui: { ...state.ui, scrollToTaskId: null } };
    case 'TOGGLE_COLLAPSE': {
      const collapsedSectionKeys = toggleKey(state.ui.collapsedSectionKeys, action.key);
      const nowCollapsed = collapsedSectionKeys.includes(action.key);
      if (!nowCollapsed) {
        return { ...state, ui: { ...state.ui, collapsedSectionKeys } };
      }
      const hiddenIds = sectionTaskIds(state, action.key);
      const selectedIds = state.ui.selectedIds.filter(id => !hiddenIds.includes(id));
      if (selectedIds.length === state.ui.selectedIds.length) {
        return { ...state, ui: { ...state.ui, collapsedSectionKeys } };
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          collapsedSectionKeys,
          selectedIds,
          selectionError: selectedIds.length > 0 ? state.ui.selectionError : null
        }
      };
    }
    case 'TOGGLE_TASK_POPUP':
      return {
        ...state,
        ui: { ...closePickers(state.ui), openTaskId: state.ui.openTaskId === action.taskId ? null : action.taskId }
      };
    case 'CLOSE_TASK_POPUP':
      return { ...state, ui: { ...closePickers(state.ui), openTaskId: null } };
    case 'START_EDIT_NAME':
      return { ...state, ui: { ...closePickers(state.ui), editingName: action.nameType } };
    case 'COMMIT_EDIT_NAME': {
      const editing = state.ui.editingName;
      const value = action.value.trim();
      if (!editing || !value) return { ...state, ui: { ...state.ui, editingName: null } };
      if (editing.type === 'category') {
        return {
          ...state,
          categories: state.categories.map(category =>
            category.id === editing.id ? { ...category, name: value } : category
          ),
          ui: { ...state.ui, editingName: null }
        };
      }
      if (editing.type === 'source') {
        return {
          ...state,
          sources: state.sources.map(source => (source.id === editing.id ? { ...source, name: value } : source)),
          ui: { ...state.ui, editingName: null }
        };
      }
      return {
        ...state,
        tasks: patchTask(state.tasks, editing.id, { title: value }),
        ui: { ...state.ui, editingName: null }
      };
    }
    case 'CANCEL_EDIT':
      return { ...state, ui: closePickers(state.ui) };
    case 'COMMIT_EMOJI':
      return {
        ...state,
        categories: state.categories.map(category =>
          category.id === action.categoryId ? { ...category, emoji: action.emoji } : category
        )
      };
    case 'CONVERT_TO_READING': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task || task.kind === 'reading') return state;
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, mutateToReading(task, action.sourceId)),
        ui: flash(
          {
            ...state.ui,
            selectedIds: removeId(state.ui.selectedIds, action.taskId),
            openTaskId: null,
            collapsedSectionKeys: removeKey(state.ui.collapsedSectionKeys, sourceSectionKey(action.sourceId)),
            scrollToTaskId: action.taskId
          },
          [action.taskId]
        )
      };
    }
    case 'CONVERT_TO_REGULAR': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task || task.kind !== 'reading') return state;
      const sectionKey = action.categoryId == null ? STANDALONE_SECTION_KEY : categorySectionKey(action.categoryId);
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, mutateToRegular(task, action.categoryId)),
        ui: flash(
          {
            ...state.ui,
            selectedIds: removeId(state.ui.selectedIds, action.taskId),
            openTaskId: null,
            collapsedSectionKeys: removeKey(state.ui.collapsedSectionKeys, sectionKey),
            scrollToTaskId: action.taskId
          },
          [action.taskId]
        )
      };
    }
    case 'TOGGLE_BULK_CONVERT': {
      const next = !state.ui.bulkConverting;
      return { ...state, ui: { ...closePickers(state.ui), bulkConverting: next } };
    }
    case 'BULK_CONVERT_TO_READING': {
      const ids = state.ui.selectedIds;
      if (ids.length === 0) return state;
      const tasks = state.tasks.map(task =>
        ids.includes(task.id) && task.kind !== 'reading' ? mutateToReading(task, action.sourceId) : task
      );
      return {
        ...state,
        tasks,
        ui: flash(
          {
            ...state.ui,
            selectedIds: [],
            selectionError: null,
            bulkConverting: false,
            collapsedSectionKeys: removeKey(state.ui.collapsedSectionKeys, sourceSectionKey(action.sourceId)),
            scrollToTaskId: ids[0]
          },
          ids
        )
      };
    }
    case 'BULK_CONVERT_TO_REGULAR': {
      const ids = state.ui.selectedIds;
      if (ids.length === 0) return state;
      const sectionKey = action.categoryId == null ? STANDALONE_SECTION_KEY : categorySectionKey(action.categoryId);
      const tasks = state.tasks.map(task =>
        ids.includes(task.id) && task.kind === 'reading' ? mutateToRegular(task, action.categoryId) : task
      );
      return {
        ...state,
        tasks,
        ui: flash(
          {
            ...state.ui,
            selectedIds: [],
            selectionError: null,
            bulkConverting: false,
            collapsedSectionKeys: removeKey(state.ui.collapsedSectionKeys, sectionKey),
            scrollToTaskId: ids[0]
          },
          ids
        )
      };
    }
    case 'REASSIGN_CATEGORY': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task || task.kind !== 'regular' || task.categoryId === action.categoryId) {
        return state;
      }
      const sectionKey = action.categoryId == null ? STANDALONE_SECTION_KEY : categorySectionKey(action.categoryId);
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, mutateCategory(task, action.categoryId)),
        ui: flash(
          {
            ...state.ui,
            selectedIds: removeId(state.ui.selectedIds, action.taskId),
            openTaskId: null,
            collapsedSectionKeys: removeKey(state.ui.collapsedSectionKeys, sectionKey),
            scrollToTaskId: action.taskId
          },
          [action.taskId]
        )
      };
    }
    case 'TOGGLE_BULK_MOVE_CATEGORY': {
      const next = !state.ui.bulkMovingCategory;
      return { ...state, ui: { ...closePickers(state.ui), bulkMovingCategory: next } };
    }
    case 'BULK_REASSIGN_CATEGORY': {
      const ids = state.ui.selectedIds;
      if (ids.length === 0) return state;
      const sectionKey = action.categoryId == null ? STANDALONE_SECTION_KEY : categorySectionKey(action.categoryId);
      const tasks = state.tasks.map(task =>
        ids.includes(task.id) && task.kind === 'regular' ? mutateCategory(task, action.categoryId) : task
      );
      return {
        ...state,
        tasks,
        ui: flash(
          {
            ...state.ui,
            selectedIds: [],
            selectionError: null,
            bulkMovingCategory: false,
            collapsedSectionKeys: removeKey(state.ui.collapsedSectionKeys, sectionKey),
            scrollToTaskId: ids[0]
          },
          ids
        )
      };
    }
    case 'SELECT_TASK': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task) return state;
      const kind = task.kind;
      const currentType = selectionType(state.tasks, state.ui.selectedIds);
      if (action.shiftKey && state.ui.lastCheckedId != null) {
        const from = action.orderedIds.indexOf(state.ui.lastCheckedId);
        const to = action.orderedIds.indexOf(action.taskId);
        if (from > -1 && to > -1) {
          if (currentType && currentType !== kind) {
            return { ...state, ui: { ...state.ui, selectionError: SELECTION_ERROR_MSG } };
          }
          const [start, end] = from < to ? [from, to] : [to, from];
          const rangeIds = action.orderedIds.slice(start, end + 1);
          let blocked = false;
          let selectedIds = [...state.ui.selectedIds];
          rangeIds.forEach(id => {
            const rangeTask = state.tasks.find(item => item.id === id);
            if (!rangeTask || rangeTask.kind !== kind) {
              blocked = true;
              return;
            }
            selectedIds = action.wantChecked ? addId(selectedIds, id) : removeId(selectedIds, id);
          });
          return {
            ...state,
            ui: {
              ...state.ui,
              selectedIds,
              selectionError: blocked ? SELECTION_ERROR_MSG : null,
              lastCheckedId: action.taskId
            }
          };
        }
      }
      if (action.wantChecked && currentType && currentType !== kind) {
        return { ...state, ui: { ...state.ui, selectionError: SELECTION_ERROR_MSG } };
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedIds: action.wantChecked
            ? addId(state.ui.selectedIds, action.taskId)
            : removeId(state.ui.selectedIds, action.taskId),
          selectionError: null,
          lastCheckedId: action.taskId
        }
      };
    }
    case 'SELECT_GROUP': {
      if (action.ids.length === 0) return state;
      const allSelected = action.ids.every(id => state.ui.selectedIds.includes(id));
      const currentType = selectionType(state.tasks, state.ui.selectedIds);
      if (!allSelected && currentType && currentType !== action.kind) {
        return { ...state, ui: { ...state.ui, selectionError: SELECTION_ERROR_MSG } };
      }
      let selectedIds = [...state.ui.selectedIds];
      if (allSelected) action.ids.forEach(id => (selectedIds = removeId(selectedIds, id)));
      else action.ids.forEach(id => (selectedIds = addId(selectedIds, id)));
      return { ...state, ui: { ...state.ui, selectedIds, selectionError: null } };
    }
    case 'CLEAR_SELECTION':
      return { ...state, ui: { ...state.ui, selectedIds: [], selectionError: null, bulkConverting: false } };
    case 'APPLY_CATEGORY_DEFAULT': {
      // Every task in the category takes the pick, including ones estimated earlier, unless the student unticked
      // "Also re-estimate", when only the tasks without a time do.
      const categories = state.categories.map(category =>
        category.id === action.categoryId ? { ...category, defaultMinutes: action.minutes } : category
      );
      const flashed: number[] = [];
      const tasks = state.tasks.map(task => {
        if (task.kind !== 'regular' || task.categoryId !== action.categoryId || task.trashed) return task;
        if (action.onlyUnestimated && task.minutes != null) return task;
        flashed.push(task.id);
        return { ...task, minutes: action.minutes, overridden: false, overriddenThisSession: false };
      });
      return {
        ...state,
        categories,
        tasks,
        ui: {
          ...flash(state.ui, flashed),
          openCustomKeys: removeKey(state.ui.openCustomKeys, `catdef-${action.categoryId}`)
        }
      };
    }
    case 'SET_TASK_MINUTES': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task) return state;
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, {
          minutes: action.minutes,
          trashed: false,
          overridden: task.categoryId != null,
          overriddenThisSession: task.categoryId != null
        }),
        ui: flash(
          {
            ...state.ui,
            openTaskId: null,
            openCustomKeys: removeKey(state.ui.openCustomKeys, `task-${action.taskId}`)
          },
          [action.taskId]
        )
      };
    }
    case 'CLEAR_TASK': {
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, {
          minutes: null,
          overridden: false,
          overriddenThisSession: false,
          estimatedAtOpen: false
        }),
        ui: flash({ ...state.ui, openTaskId: null }, [action.taskId])
      };
    }
    case 'RESET_TO_DEFAULT': {
      // Back to following the category: its pick from this visit, or else its time from last visit, or no time yet.
      const task = state.tasks.find(item => item.id === action.taskId);
      const category = task?.categoryId != null ? state.categories.find(item => item.id === task.categoryId) : null;
      if (!task || !category) return state;
      const minutes = category.defaultMinutes ?? category.priorMinutes;
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, {
          overridden: false,
          overriddenThisSession: false,
          minutes,
          ...(minutes == null ? { estimatedAtOpen: false } : {})
        }),
        ui: flash(state.ui, [action.taskId])
      };
    }
    case 'TRASH_TASK': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task) return state;
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, applyTrash(task)),
        ui: flash({ ...state.ui, openTaskId: null }, [action.taskId])
      };
    }
    case 'UNTRASH_TASK':
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, { trashed: false }),
        ui: flash(state.ui, [action.taskId])
      };
    case 'BULK_SET_MINUTES': {
      const ids = state.ui.selectedIds;
      const tasks = state.tasks.map(task =>
        ids.includes(task.id)
          ? {
              ...task,
              minutes: action.minutes,
              trashed: false,
              overridden: task.categoryId != null,
              overriddenThisSession: task.categoryId != null
            }
          : task
      );
      return {
        ...state,
        tasks,
        ui: flash({ ...state.ui, selectedIds: [], selectionError: null }, ids)
      };
    }
    case 'BULK_USE_DEFAULT': {
      const ids = state.ui.selectedIds;
      const flashed: number[] = [];
      const tasks = state.tasks.map(task => {
        if (!ids.includes(task.id) || task.categoryId == null) return task;
        const category = state.categories.find(item => item.id === task.categoryId);
        if (category?.defaultMinutes == null) return task;
        flashed.push(task.id);
        return {
          ...task,
          overridden: false,
          overriddenThisSession: false,
          minutes: category.defaultMinutes,
          trashed: false
        };
      });
      return { ...state, tasks, ui: flash({ ...state.ui, selectedIds: [], selectionError: null }, flashed) };
    }
    case 'BULK_TRASH': {
      const ids = state.ui.selectedIds;
      const tasks = state.tasks.map(task => (ids.includes(task.id) ? applyTrash(task) : task));
      return { ...state, tasks, ui: flash({ ...state.ui, selectedIds: [], selectionError: null }, ids) };
    }
    case 'SET_PAGES': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task || task.kind !== 'reading' || task.sourceId == null) return state;
      const source = state.sources.find(item => item.id === task.sourceId);
      if (!source) return state;
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, {
          pages: action.pages,
          from: action.from,
          to: action.to,
          rangeLabel: action.rangeLabel,
          rawPagesInput: action.rawPagesInput,
          trashed: false,
          minutes: readingMinutes(action.pages, source.secondsPerPage)
        }),
        ui: flash(
          {
            ...state.ui,
            openTaskId: null,
            openCustomKeys: removeKey(state.ui.openCustomKeys, `pages-${action.taskId}`)
          },
          [action.taskId]
        )
      };
    }
    case 'CLEAR_PAGES':
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, {
          pages: null,
          from: null,
          to: null,
          rangeLabel: null,
          rawPagesInput: null,
          minutes: null,
          estimatedAtOpen: false
        }),
        ui: flash({ ...state.ui, openTaskId: null }, [action.taskId])
      };
    case 'RESTORE_DETECTED': {
      const task = state.tasks.find(item => item.id === action.taskId);
      if (!task || task.detectedPages == null || task.sourceId == null) return state;
      const source = state.sources.find(item => item.id === task.sourceId);
      if (!source) return state;
      return {
        ...state,
        tasks: patchTask(state.tasks, action.taskId, {
          pages: task.detectedPages,
          from: task.detectedFrom,
          to: task.detectedTo,
          rangeLabel: task.detectedRangeLabel,
          rawPagesInput: task.detectedRawPagesInput,
          minutes: readingMinutes(task.detectedPages, source.secondsPerPage)
        }),
        ui: flash({ ...state.ui, openTaskId: null }, [action.taskId])
      };
    }
    case 'RESET_SOURCE_DETECTED': {
      const source = state.sources.find(item => item.id === action.sourceId);
      if (!source) return state;
      const flashed: number[] = [];
      const tasks = state.tasks.map(task => {
        if (task.kind !== 'reading' || task.sourceId !== action.sourceId || task.trashed) return task;
        const newPages = task.detectedPages;
        if (task.pages === newPages && task.from === task.detectedFrom && task.to === task.detectedTo) return task;
        flashed.push(task.id);
        return {
          ...task,
          pages: newPages,
          from: newPages != null ? task.detectedFrom : null,
          to: newPages != null ? task.detectedTo : null,
          rangeLabel: newPages != null ? task.detectedRangeLabel : null,
          rawPagesInput: newPages != null ? task.detectedRawPagesInput : null,
          minutes: readingMinutes(newPages, source.secondsPerPage)
        };
      });
      if (flashed.length === 0) return state;
      return { ...state, tasks, ui: flash(state.ui, flashed) };
    }
    case 'SET_RATE': {
      const sources = state.sources.map(source =>
        source.id === action.sourceId ? { ...source, secondsPerPage: action.secondsPerPage } : source
      );
      const { tasks, flashed } = recomputeSourceMinutes(state.tasks, sources, action.sourceId);
      const toast = flashed.length > 0 ? `Recalculated ${flashed.length} readings` : 'Saved for future readings';
      return {
        ...state,
        sources,
        tasks,
        ui: {
          ...flash(state.ui, flashed),
          toasts: { ...state.ui.toasts, [`rate-${action.sourceId}`]: toast },
          openCustomKeys: removeKey(state.ui.openCustomKeys, `rate-${action.sourceId}`)
        }
      };
    }
    case 'BULK_SET_PAGES': {
      const ids = state.ui.selectedIds;
      const flashed: number[] = [];
      const tasks = state.tasks.map(task => {
        if (!ids.includes(task.id) || task.kind !== 'reading' || task.sourceId == null) return task;
        const source = state.sources.find(item => item.id === task.sourceId);
        if (!source) return task;
        flashed.push(task.id);
        return {
          ...task,
          pages: action.pages,
          from: action.from,
          to: action.to,
          rangeLabel: action.rangeLabel,
          rawPagesInput: action.rawPagesInput,
          trashed: false,
          minutes: readingMinutes(action.pages, source.secondsPerPage)
        };
      });
      return { ...state, tasks, ui: flash({ ...state.ui, selectedIds: [], selectionError: null }, flashed) };
    }
    case 'BULK_RESTORE_DETECTED': {
      const ids = state.ui.selectedIds;
      const flashed: number[] = [];
      const tasks = state.tasks.map(task => {
        if (!ids.includes(task.id) || task.detectedPages == null || task.sourceId == null) return task;
        const source = state.sources.find(item => item.id === task.sourceId);
        if (!source) return task;
        flashed.push(task.id);
        return {
          ...task,
          pages: task.detectedPages,
          from: task.detectedFrom,
          to: task.detectedTo,
          rangeLabel: task.detectedRangeLabel,
          rawPagesInput: task.detectedRawPagesInput,
          trashed: false,
          minutes: readingMinutes(task.detectedPages, source.secondsPerPage)
        };
      });
      return { ...state, tasks, ui: flash({ ...state.ui, selectedIds: [], selectionError: null }, flashed) };
    }
    case 'RESET_CATEGORY': {
      const category = state.categories.find(item => item.id === action.categoryId);
      if (!category) return state;
      const flashed: number[] = [];
      let changed = false;
      const tasks = state.tasks.map(task => {
        if (task.kind !== 'regular' || task.categoryId !== action.categoryId) return task;
        const needsValueReset = task.minutes != null || task.overridden || task.trashed;
        if (!needsValueReset && !task.estimatedAtOpen) return task;
        changed = true;
        if (needsValueReset) flashed.push(task.id);
        return {
          ...task,
          minutes: null,
          overridden: false,
          overriddenThisSession: false,
          trashed: false,
          estimatedAtOpen: false
        };
      });
      const defaultChanged = category.defaultMinutes != null;
      if (!changed && !defaultChanged) return state;
      return {
        ...state,
        categories: state.categories.map(item => (item.id === action.categoryId ? { ...item, defaultMinutes: null } : item)),
        tasks,
        ui: flash(state.ui, flashed)
      };
    }
    case 'RESET_STANDALONE': {
      const flashed: number[] = [];
      let changed = false;
      const tasks = state.tasks.map(task => {
        if (task.kind !== 'regular' || task.categoryId != null) return task;
        const needsValueReset = task.minutes != null || task.overridden || task.trashed;
        if (!needsValueReset && !task.estimatedAtOpen) return task;
        changed = true;
        if (needsValueReset) flashed.push(task.id);
        return {
          ...task,
          minutes: null,
          overridden: false,
          overriddenThisSession: false,
          trashed: false,
          estimatedAtOpen: false
        };
      });
      if (!changed) return state;
      return { ...state, tasks, ui: flash(state.ui, flashed) };
    }
    case 'TOGGLE_CUSTOM':
      return { ...state, ui: { ...state.ui, openCustomKeys: toggleKey(state.ui.openCustomKeys, action.key) } };
    case 'SYNC_FROM_SERVER': {
      const removed = action.changes.filter(change => change.remove).map(change => change.id);
      const titles = { ...state.originals.titles };
      removed.forEach(id => delete titles[id]);
      let sources = state.sources;
      let tasks = state.tasks.filter(task => !removed.includes(task.id));
      action.changes.forEach(change => {
        if (change.remove) return;
        const { source } = change;
        if (source && !sources.some(item => item.id === source.id)) sources = [...sources, source];
        tasks = tasks.map(task => {
          if (task.id !== change.id) return task;
          const next = change.replace ?? { ...task, ...change.patch };
          // The saved title is now the one to compare against, so Save doesn't send it again.
          titles[task.id] = next.title;
          if (next.kind !== 'reading') return next;
          // A reading's time follows its book's pace in the Estimator, which may have changed and not been saved yet.
          const rate = sources.find(item => item.id === next.sourceId)?.secondsPerPage;
          return rate == null ? next : { ...next, minutes: readingMinutes(next.pages, rate) };
        });
      });
      // A category made in the task's own dialog comes along with the task moved into it.
      const categories = [
        ...state.categories,
        ...action.courseCategories.filter(
          category =>
            !state.categories.some(item => item.id === category.id) && tasks.some(task => task.categoryId === category.id)
        )
      ];
      return {
        ...state,
        categories,
        sources,
        tasks,
        originals: { ...state.originals, titles },
        ui: {
          ...state.ui,
          openTaskId: removed.includes(state.ui.openTaskId ?? -1) ? null : state.ui.openTaskId,
          selectedIds: state.ui.selectedIds.filter(id => !removed.includes(id))
        }
      };
    }
    default:
      return state;
  }
};

const sectionTaskIds = (state: EstimatorState, key: string): number[] => {
  if (key === STANDALONE_SECTION_KEY) {
    return state.tasks.filter(task => task.kind === 'regular' && task.categoryId == null).map(task => task.id);
  }
  const source = state.sources.find(item => sourceSectionKey(item.id) === key);
  if (source) {
    return state.tasks.filter(task => task.kind === 'reading' && task.sourceId === source.id).map(task => task.id);
  }
  const categoryId = Number(key);
  if (state.categories.some(item => item.id === categoryId)) {
    return state.tasks.filter(task => task.kind === 'regular' && task.categoryId === categoryId).map(task => task.id);
  }
  return [];
};

const categoryHasAnyValue = (category: EstimatorCategory, tasks: EstimatorTask[]) =>
  category.defaultMinutes != null ||
  tasks.some(
    task =>
      task.kind === 'regular' && task.categoryId === category.id && (task.minutes != null || task.overridden || task.trashed)
  );

const sourceHasChangeFromDetected = (sourceId: number, tasks: EstimatorTask[]) =>
  tasks.some(task => {
    if (task.kind !== 'reading' || task.sourceId !== sourceId || task.trashed) return false;
    return task.pages !== task.detectedPages || task.from !== task.detectedFrom || task.to !== task.detectedTo;
  });

const readingWasOverridden = (task: EstimatorTask) => task.detectedPages != null && task.pages !== task.detectedPages;

export const useTaskEstimator = (open: boolean, courseId: number | null) => {
  const courses = useSelector(getAllCourses);
  const workloadTasks = useSelector((state: RootState) => state.workload.tasks);
  const course = useMemo(() => (courseId != null ? courses.find(item => item.id === courseId) : undefined), [
    courses,
    courseId
  ]);
  // What finished tasks in each category took, from the time marked used on them, so a re-estimate can lean on it.
  const finishedByCategory = useMemo(() => {
    const byCategory = new Map<number, { count: number; minutes: number }>();
    if (courseId == null) return byCategory;
    workloadTasks.forEach(task => {
      const categoryId = task.taskCategory?.id;
      if (task.courseId !== courseId || task.deleted || task.reading || task.state !== TaskState.COMPLETED) return;
      if (categoryId == null || !(task.timeSpent > 0)) return;
      const entry = byCategory.get(categoryId) || { count: 0, minutes: 0 };
      byCategory.set(categoryId, { count: entry.count + 1, minutes: entry.minutes + task.timeSpent });
    });
    return byCategory;
  }, [workloadTasks, courseId]);
  const [state, dispatch] = useReducer(reducer, emptyState());
  const flashRef = useRef<number[]>([]);
  const snapshotRef = useRef<{ courseId: number; hasTasks: boolean } | null>(null);
  // Each task as last saved, to tell what a save made while the Estimator is open (in the task's own dialog, opened
  // from its row) changed about it.
  const savedRef = useRef<Map<number, WorkloadTask>>(new Map());

  useEffect(() => {
    if (!open || courseId == null) {
      snapshotRef.current = null;
      return;
    }
    if (!course) return;

    const payload = mapWorkloadToEstimator(course, workloadTasks);
    const session = snapshotRef.current;
    const isNewSession = session?.courseId !== courseId;
    const remember = () => {
      const ids = new Set(payload.tasks.map(task => task.id));
      savedRef.current = new Map(workloadTasks.filter(task => ids.has(task.id)).map(task => [task.id, task]));
    };

    if (isNewSession) {
      dispatch({ type: 'RESET', payload });
      snapshotRef.current = { courseId, hasTasks: payload.tasks.length > 0 };
      remember();
      return;
    }

    // First paint can race the workload fetch after import; fill in once tasks arrive.
    if (session && !session.hasTasks && payload.tasks.length > 0) {
      dispatch({ type: 'RESET', payload });
      snapshotRef.current = { courseId, hasTasks: true };
      remember();
      return;
    }

    // A task saved since (from its own dialog, say): take in what changed, and nothing else.
    const byId = new Map(workloadTasks.map(task => [task.id, task]));
    const changes: ServerTaskChange[] = [];
    savedRef.current.forEach((before, id) => {
      const after = byId.get(id);
      if (after === before) return;
      const change = serverTaskChange(course, before, after);
      if (change) changes.push(change);
      if (after && !change?.remove) savedRef.current.set(id, after);
      else savedRef.current.delete(id);
    });
    if (changes.length > 0) dispatch({ type: 'SYNC_FROM_SERVER', changes, courseCategories: payload.categories });
    // Snapshot on open / courseId / course presence. Re-run when workload arrives so an
    // empty first paint can hydrate. Do not depend on `course` identity (calendar refresh
    // would wipe in-progress edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId, Boolean(course), workloadTasks]);

  useEffect(() => {
    const ids = state.ui.flashingIds.filter(id => !flashRef.current.includes(id));
    flashRef.current = state.ui.flashingIds;
    if (ids.length === 0) return undefined;
    const timer = window.setTimeout(() => dispatch({ type: 'CLEAR_FLASH', ids }), 650);
    return () => window.clearTimeout(timer);
  }, [state.ui.flashingIds]);

  const regularTasks = state.tasks.filter(task => task.kind === 'regular');
  const readingTasks = state.tasks.filter(task => task.kind === 'reading');
  const standaloneTasks = regularTasks.filter(task => task.categoryId == null);
  const tasksByCategory = (categoryId: number) => regularTasks.filter(task => task.categoryId === categoryId);
  const tasksBySource = (sourceId: number) => readingTasks.filter(task => task.sourceId === sourceId);

  const visibleOrder = useMemo(() => {
    const ids: number[] = [];
    state.categories.forEach(category => {
      if (state.ui.collapsedSectionKeys.includes(categorySectionKey(category.id))) return;
      tasksByCategory(category.id).forEach(task => ids.push(task.id));
    });
    state.sources.forEach(source => {
      if (state.ui.collapsedSectionKeys.includes(sourceSectionKey(source.id))) return;
      tasksBySource(source.id).forEach(task => ids.push(task.id));
    });
    if (!state.ui.collapsedSectionKeys.includes(STANDALONE_SECTION_KEY)) {
      standaloneTasks.forEach(task => ids.push(task.id));
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.categories, state.sources, state.tasks, state.ui.collapsedSectionKeys]);

  const activeTasks = state.tasks.filter(task => !task.trashed);
  const estimatedCount = activeTasks.filter(task => task.minutes != null).length;
  const remainingCount = activeTasks.length - estimatedCount;
  const totalMinutes = activeTasks.reduce((sum, task) => sum + (task.minutes || 0), 0);
  const selectedType = selectionType(state.tasks, state.ui.selectedIds);
  const saveDisabled = estimatedCount === 0 && activeTasks.length > 0;

  const estimateRequest = useMemo(() => (courseId != null ? buildEstimateRequest(state, courseId) : null), [
    state,
    courseId
  ]);

  const selectTask = useCallback(
    (taskId: number, wantChecked: boolean, shiftKey?: boolean) => {
      dispatch({ type: 'SELECT_TASK', taskId, wantChecked, orderedIds: visibleOrder, shiftKey: !!shiftKey });
    },
    [visibleOrder]
  );

  return {
    course,
    workloadTasks,
    state,
    dispatch,
    readingTasks,
    standaloneTasks,
    tasksByCategory,
    tasksBySource,
    categoryHasAnyValue: (category: EstimatorCategory) => categoryHasAnyValue(category, state.tasks),
    finishedInCategory: (categoryId: number) => finishedByCategory.get(categoryId) ?? null,
    sourceHasChangeFromDetected: (sourceId: number) => sourceHasChangeFromDetected(sourceId, state.tasks),
    readingWasOverridden,
    activeTasks,
    estimatedCount,
    remainingCount,
    totalMinutes,
    selectedType,
    saveDisabled,
    estimateRequest,
    selectTask
  };
};

export type EstimatorAction = Action;
export type EstimatorDispatch = (action: Action) => void;
export type TaskEstimatorModel = ReturnType<typeof useTaskEstimator>;
